import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useOrganization } from './OrganizationContext';
import { useAppSelector } from '@/hooks/useRedux';
import { config } from '@/config/env';
import { setERPBaseUrl, resetERPClient } from '@/services/clients';

/**
 * FPO Configuration returned from farmers-service
 */
interface FPOConfig {
  fpo_id: string;
  fpo_name: string;
  erp_base_url: string;
  erp_api_version: string;
  features: {
    ecommerce_enabled?: boolean;
    inventory_management?: boolean;
    credit_sales?: boolean;
  };
}

interface ERPConfigContextType {
  config: FPOConfig | null;
  erpBaseUrl: string;
  isLoading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
}

const ERPConfigContext = createContext<ERPConfigContextType | undefined>(undefined);

export const useERPConfig = () => {
  const context = useContext(ERPConfigContext);
  if (!context) {
    throw new Error('useERPConfig must be used within ERPConfigProvider');
  }
  return context;
};

interface ERPConfigProviderProps {
  children: ReactNode;
}

/**
 * ERPConfigProvider fetches the FPO configuration from farmers-service
 * which includes the dynamic erp_base_url for the selected organization.
 */
export const ERPConfigProvider: React.FC<ERPConfigProviderProps> = ({ children }) => {
  const [fpoConfig, setFpoConfig] = useState<FPOConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start as loading
  const [error, setError] = useState<string | null>(null);

  const { selectedOrg } = useOrganization();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const orgId = selectedOrg?.id;

  // Track previous org to detect changes
  const prevOrgIdRef = useRef<string | null>(null);

  const fetchConfig = async () => {
    if (!orgId || !accessToken) {
      console.log('[ERPConfig] Cannot fetch config - missing orgId:', orgId, 'or accessToken:', !!accessToken);
      setFpoConfig(null);
      setIsLoading(false);
      return;
    }

    console.log('[ERPConfig] Fetching config for org:', orgId);
    setIsLoading(true);
    setError(null);

    try {
      // Check localStorage cache first
      const cacheKey = `fpo_config_${orgId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);

      // Use cache if less than 30 minutes old
      if (cached && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        if (age < 30 * 60 * 1000) {
          console.log('[ERPConfig] Using cached config for org:', orgId);
          setFpoConfig(JSON.parse(cached));
          setIsLoading(false);
          return;
        }
      }

      // Fetch from farmers-service using self-access endpoint
      // Endpoint: /api/v1/me/organization/configuration?org_id={orgId}
      // This endpoint doesn't require fpo:read permission - only authentication
      const farmersServiceUrl = config.farmersServiceUrl;
      const response = await fetch(
        `${farmersServiceUrl}/api/v1/me/organization/configuration?org_id=${orgId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch FPO config: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      console.log('[ERPConfig] Raw API response:', JSON.stringify(data, null, 2));

      if (data.success && data.data) {
        const configData: FPOConfig = {
          fpo_id: data.data.fpo_id || orgId,
          fpo_name: data.data.fpo_name || selectedOrg?.name || 'Unknown FPO',
          erp_base_url: data.data.erp_base_url,
          erp_api_version: data.data.erp_api_version || 'v1',
          features: data.data.features || {},
        };

        console.log('[ERPConfig] Parsed config:', {
          fpo_id: configData.fpo_id,
          fpo_name: configData.fpo_name,
          erp_base_url: configData.erp_base_url,
          erp_api_version: configData.erp_api_version,
        });

        setFpoConfig(configData);

        // Cache the config
        localStorage.setItem(cacheKey, JSON.stringify(configData));
        localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());

        console.log('[ERPConfig] Loaded config for org:', orgId, 'ERP URL:', configData.erp_base_url);
      } else {
        throw new Error(data.message || 'Failed to fetch FPO configuration');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('[ERPConfig] Error fetching FPO config:', err);

      // Try to use stale cache as fallback
      const cacheKey = `fpo_config_${orgId}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        console.warn('[ERPConfig] Using stale cache due to error');
        setFpoConfig(JSON.parse(cached));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Reset ERP client when org changes (including on logout/login)
  useEffect(() => {
    if (prevOrgIdRef.current !== null && prevOrgIdRef.current !== orgId) {
      console.log('[ERPConfig] Organization changed from', prevOrgIdRef.current, 'to', orgId, '- resetting ERP client');
      resetERPClient();
      setFpoConfig(null);
    }
    prevOrgIdRef.current = orgId || null;
  }, [orgId]);

  useEffect(() => {
    fetchConfig();
  }, [orgId, accessToken]);

  // Compute the effective ERP base URL (from FPO config only - no hardcoded fallback)
  const erpBaseUrl = fpoConfig?.erp_base_url || '';

  // Update the global ERP base URL whenever it changes
  // This ensures erpClient (imported directly by hooks) uses the correct URL
  useEffect(() => {
    if (erpBaseUrl) {
      console.log('[ERPConfig] Setting global ERP base URL:', erpBaseUrl);
      setERPBaseUrl(erpBaseUrl);
    } else if (fpoConfig === null && !isLoading) {
      // Config loaded but no URL - reset
      console.log('[ERPConfig] No ERP URL in config, resetting client');
      resetERPClient();
    }
  }, [erpBaseUrl, fpoConfig, isLoading]);

  const value: ERPConfigContextType = {
    config: fpoConfig,
    erpBaseUrl,
    isLoading,
    error,
    refreshConfig: fetchConfig,
  };

  return (
    <ERPConfigContext.Provider value={value}>
      {children}
    </ERPConfigContext.Provider>
  );
};
